import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { ThemeService } from '@ld.consumer-portal/utils';

function nativeBrowserEmailValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const tempInput = document.createElement('input');
  tempInput.type = 'email';
  tempInput.value = control.value;

  // Use the native input only to detect an invalid format; its validationMessage is untranslatable
  // browser UI text (locale follows the browser, not the app), so the template renders a Transloco
  // key instead of this message.
  if (!tempInput.validity.valid) {
    return { nativeEmailError: true };
  }

  return null;
}

@Component({
  selector: 'ld-forgot-password-card',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslocoDirective,
  ],
  templateUrl: './forgot-password-card.component.html',
  styleUrl: './forgot-password-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordCardComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly themeService = inject(ThemeService);

  protected logoUrl = computed(() => this.themeService.getLogoUrl());
  protected partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());

  initialEmail = input('');
  isLoading = input(false);
  externalErrorMessage = input<string | null>(null);
  title = input('');
  subtitle = input('');

  submitEmail = output<string>();
  backToLogin = output<void>();

  loading = computed(() => this.isLoading());
  errorMessage = computed(() => this.externalErrorMessage());

  forgotPasswordForm = this.fb.group(
    {
      email: ['', [Validators.required, nativeBrowserEmailValidator]],
    },
    { updateOn: 'blur' },
  );

  ngOnInit(): void {
    const email = this.initialEmail();
    if (email) {
      this.forgotPasswordForm.patchValue({ email });
    }
  }

  protected onSubmit(): void {
    this.syncAutofillValues();

    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    const email = this.forgotPasswordForm.get('email')?.value ?? '';
    this.submitEmail.emit(email);
  }

  protected onEnterKey(event: Event): void {
    event.preventDefault();
    this.syncAutofillValues();
    this.forgotPasswordForm.markAllAsTouched();
    this.forgotPasswordForm.updateValueAndValidity();
    if (this.forgotPasswordForm.valid) {
      this.onSubmit();
    }
  }

  private syncAutofillValues(): void {
    const emailInput = document.getElementById('forgot-password-email-input') as HTMLInputElement;
    if (emailInput?.value && emailInput.value !== this.forgotPasswordForm.get('email')?.value) {
      this.forgotPasswordForm.get('email')?.setValue(emailInput.value);
    }
  }

  protected onBackToLogin(): void {
    this.backToLogin.emit();
  }
}
